import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import {
  deleteDashboardCache,
  deletePartnerDashboardCache,
  deletePlatformDashboardCache,
  fetchDashboardCaches,
  fetchPartnerDashboardCaches,
  fetchPlatformDashboardCaches,
} from "../../api/client";
import {
  formatDashboardCacheExpiresLabel,
  formatDashboardPeriodLabel,
} from "../../utils/dashboardDeepLink";

function formatWorkspaceScopeLabel(entry, t) {
  if (entry?.scopeLabel) return entry.scopeLabel;
  const names = (entry?.workspaces || [])
    .map((w) => String(w?.dominio || "").trim())
    .filter(Boolean);
  if (entry?.scopeMode === "all" || !entry?.dominioIds?.length) {
    const count = Number(entry?.workspaceCount) || names.length || 0;
    return t("dashboardCaches.scopeAll", {
      count,
      defaultValue: "Todos os workspaces ({{count}})",
    });
  }
  if (names.length === 1) return names[0];
  if (names.length > 1 && names.length <= 3) return names.join(", ");
  const count = Number(entry?.workspaceCount) || entry?.dominioIds?.length || names.length || 0;
  return t("dashboardCaches.scopeSelected", {
    count,
    defaultValue: "{{count}} selecionados",
  });
}

function workspaceDashboardTabPath(tabKey) {
  return `/dashboard/${tabKey}`;
}

function buildOpenPath(entry, variant) {
  const tabKey = entry?.tabKey;
  if (!tabKey) return null;

  const params = new URLSearchParams();
  if (entry.start) params.set("start", entry.start);
  if (entry.end) params.set("end", entry.end);
  if (Array.isArray(entry.dominioIds) && entry.dominioIds.length) {
    params.set("dominio_ids", entry.dominioIds.map((x) => String(x)).join(","));
  }
  if (tabKey === "surveys") {
    if (variant === "platform" && entry.platformSurveyKey) {
      params.set("survey", entry.platformSurveyKey);
    } else if (variant === "partner" && entry.partnerSurveyKey) {
      params.set("survey", entry.partnerSurveyKey);
    } else if (entry.surveyId) {
      params.set("survey", String(entry.surveyId));
    }
  }

  const qs = params.toString();
  const base =
    variant === "platform"
      ? `/platform/xprobase/dashboard/${tabKey}`
      : variant === "partner"
        ? `/partner/console/dashboard/${tabKey}`
        : workspaceDashboardTabPath(tabKey);
  return qs ? `${base}?${qs}` : base;
}

export default function DashboardCachesDialog({ open, onClose, variant = "workspace" }) {
  const { t, i18n } = useTranslation("settings");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = React.useState(null);
  const [error, setError] = React.useState("");

  const isPartner = variant === "partner";
  const isPlatform = variant === "platform";
  const queryKey = isPlatform
    ? ["platform-dashboard-caches"]
    : isPartner
      ? ["partner-dashboard-caches"]
      : ["dashboard-caches"];
  const fetchFn = isPlatform
    ? fetchPlatformDashboardCaches
    : isPartner
      ? fetchPartnerDashboardCaches
      : fetchDashboardCaches;
  const deleteFn = isPlatform
    ? deletePlatformDashboardCache
    : isPartner
      ? deletePartnerDashboardCache
      : deleteDashboardCache;

  const { data, isLoading, isFetching, refetch } = useQuery(queryKey, fetchFn, {
    enabled: open,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const caches = data?.caches || [];
  const locale = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";

  React.useEffect(() => {
    if (!open) {
      setError("");
      setDeletingId(null);
    }
  }, [open]);

  const invalidateDashboardQueries = () => {
    if (isPlatform) {
      queryClient.invalidateQueries(["platform-dashboard"]);
      queryClient.invalidateQueries(["platform-dashboard-preview"]);
      queryClient.invalidateQueries(["platform-survey-analytics"]);
      queryClient.invalidateQueries(["platform-survey-analytics-preview"]);
    } else if (isPartner) {
      queryClient.invalidateQueries(["partner-dashboard"]);
      queryClient.invalidateQueries(["partner-dashboard-preview"]);
      queryClient.invalidateQueries(["partner-survey-analytics"]);
      queryClient.invalidateQueries(["partner-survey-analytics-preview"]);
    } else {
      queryClient.invalidateQueries(["dashboard"]);
      queryClient.invalidateQueries(["dashboard-preview"]);
      queryClient.invalidateQueries(["survey-analytics"]);
      queryClient.invalidateQueries(["survey-analytics-preview"]);
    }
    queryClient.invalidateQueries(queryKey);
  };

  const handleDelete = async (entryId) => {
    setDeletingId(entryId);
    setError("");
    try {
      await deleteFn(entryId);
      invalidateDashboardQueries();
      await refetch();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          t("dashboardCaches.deleteError", { defaultValue: "Não foi possível remover o cache." })
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpen = (entry) => {
    const path = buildOpenPath(entry, variant);
    if (!path) return;
    onClose?.();
    navigate(path);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("dashboardCaches.title", { defaultValue: "Relatórios em cache" })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("dashboardCaches.subtitle", {
            defaultValue:
              "Relatórios que você gerou e que ainda estão disponíveis para consulta. Ao apagar, será necessário gerar novamente.",
          })}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {isLoading || isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !caches.length ? (
          <Alert severity="info">
            {t("dashboardCaches.empty", {
              defaultValue: "Nenhum relatório em cache no momento.",
            })}
          </Alert>
        ) : (
          <List disablePadding>
            {caches.map((entry) => {
              const period = formatDashboardPeriodLabel(entry.start, entry.end, locale);
              const generated = entry.computedAt
                ? formatDashboardCacheExpiresLabel(entry.computedAt, locale)
                : "";
              const expires = entry.expiresAt
                ? formatDashboardCacheExpiresLabel(entry.expiresAt, locale)
                : "";
              const secondary = [
                period,
                formatWorkspaceScopeLabel(entry, t),
                generated
                  ? t("dashboardCaches.generatedAt", {
                      defaultValue: "Gerado em {{date}}",
                      date: generated,
                    })
                  : null,
                expires
                  ? t("dashboardCaches.expiresAt", {
                      defaultValue: "Válido até {{date}}",
                      date: expires,
                    })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ");

              const openPath = buildOpenPath(entry, variant);
              const isDeleting = deletingId === entry.entryId;

              return (
                <ListItem
                  key={entry.entryId}
                  divider
                  secondaryAction={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {openPath ? (
                        <IconButton
                          size="small"
                          aria-label={t("dashboardCaches.open", { defaultValue: "Abrir relatório" })}
                          onClick={() => handleOpen(entry)}
                        >
                          <OpenInNewOutlinedIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t("dashboardCaches.delete", { defaultValue: "Apagar cache" })}
                        disabled={isDeleting}
                        onClick={() => handleDelete(entry.entryId)}
                      >
                        {isDeleting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Stack>
                  }
                  sx={{ pr: 12 }}
                >
                  <ListItemText
                    primary={entry.title || entry.route}
                    secondary={secondary}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.close", { defaultValue: "Fechar" })}</Button>
      </DialogActions>
    </Dialog>
  );
}
